/**
 * The hostnames the app derives from its stage and base domain.
 *
 * Mirrors backend/lib/util/domains.py. Both read what infra/site.js builds in:
 *
 *   VITE_STAGE                 the legacy stage (`prod`, `test`, `qa-a`, `ca`)
 *   VITE_BASE_DOMAIN           `chartflow.ca` on a stage with a domain of its
 *                              own. ABSENT on every other stage, which means
 *                              `chartflow.io`; those builds are unchanged.
 *   VITE_PRODUCTION_HOSTNAMES  `'true'` on a regional production stage, which
 *                              owns its base domain, so no hostname carries the
 *                              stage (`www.chartflow.ca`, `ws.chartflow.ca`,
 *                              `missouri.chartflow.ca`).
 *   VITE_SITE_URL              the stage's own site origin (infra/site.js), read
 *                              by siteOrigin only.
 *
 * Every function takes the env as an argument (default: the build's) so the
 * parsing can be tested with any host.
 */

export const DEFAULT_BASE_DOMAIN = 'chartflow.io'

// First labels that are the app itself, not an organization's subdomain.
const NON_ORGANIZATION_LABELS = ['www', 'chartflow', 'dev', 'mike-local']

export function domainsFromEnv(env = import.meta.env) {
  return {
    stage: env.VITE_STAGE,
    baseDomain: env.VITE_BASE_DOMAIN || DEFAULT_BASE_DOMAIN,
    productionHostnames: env.VITE_PRODUCTION_HOSTNAMES === 'true'
  }
}

/**
 * The organization subdomain a hostname addresses, or null when it addresses the
 * app itself (`www.`, the stage's own host, the apex, a dev host).
 *
 * Behavior on chartflow.io is exactly what Login.vue and the router each did
 * inline: a host that starts with the stage name or with `www` is the app, and
 * otherwise the first label is the organization unless it is one of the app's
 * own. (`startsWith` and not equality is how it always was; it means an
 * organization named like the stage, `testschool` on test, is not found.)
 *
 * A stage that owns its domain has no stage-named host, so the stage prefix is
 * not consulted for it: with stage `ca`, `california.chartflow.ca` is an
 * organization, not the app.
 */
export function organizationSubdomain(hostname, env = import.meta.env) {
  const { stage, productionHostnames } = domainsFromEnv(env)
  const isApp =
    (!productionHostnames && stage !== undefined && hostname.startsWith(stage)) ||
    hostname.startsWith('www')
  if (isApp) return null
  const subdomain = hostname.split('.')[0]
  return NON_ORGANIZATION_LABELS.includes(subdomain) ? null : subdomain
}

/** The websocket front door: `wss://ws.test.chartflow.io`, `wss://ws.chartflow.ca`. */
export function webSocketUrl(env = import.meta.env) {
  const { stage, baseDomain, productionHostnames } = domainsFromEnv(env)
  return productionHostnames ? `wss://ws.${baseDomain}` : `wss://ws.${stage}.${baseDomain}`
}

/** The SAML service-provider entity id, the same on every stage of a domain. */
export function samlEntityId(env = import.meta.env) {
  return `https://${domainsFromEnv(env).baseDomain}/sp`
}

/**
 * The origin of the stage's own marketing site, with no trailing slash:
 * `https://www.chartflow.io` on production, `https://qa-a.chartflow.io` on a QA
 * slot. This is what a page's canonical URL is built on: an organization's
 * subdomain serves the same marketing HTML, and its canonical must still name
 * the stage's own host, not the one the visitor arrived on.
 *
 * VITE_SITE_URL (set by infra/site.js from the same stage inputs) wins; without
 * it the origin is derived from the stage as infra/stage.js does. Returns null
 * when the build has neither, so a page can leave its canonical out rather than
 * guess one.
 */
export function siteOrigin(env = import.meta.env) {
  if (env.VITE_SITE_URL) return env.VITE_SITE_URL.replace(/\/+$/, '')
  const { stage, baseDomain, productionHostnames } = domainsFromEnv(env)
  if (!stage) return null
  return productionHostnames || stage === 'prod' ? `https://www.${baseDomain}` : `https://${stage}.${baseDomain}`
}
